<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import NooviConnectAPI from 'dashboard/api/nooviConnect';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  accountId: {
    type: [String, Number],
    required: true,
  },
  inboxId: {
    type: [String, Number],
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  hint: {
    type: String,
    default: '',
  },
  showSkipAction: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['connected', 'skip']);

const { t } = useI18n();

const STATUS_POLL_MS = 3000;
const QR_REFRESH_SECONDS = 20;

const tab = ref('qr');
const qrSrc = ref('');
const qrLoading = ref(false);
const sessionStatus = ref('');
const phone = ref('');
const pairCode = ref('');
const pairing = ref(false);
const statusTimer = ref(null);
const qrTimer = ref(null);
const qrRefreshIn = ref(QR_REFRESH_SECONDS);
const errorMessage = ref('');
const connectedPayload = ref(null);

const isConnected = computed(() => sessionStatus.value === 'working');
const digitsPhone = computed(() => phone.value.replace(/\D/g, ''));
const connectionTitle = computed(
  () => props.title || t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CONNECT_TITLE')
);
const connectionHint = computed(
  () => props.hint || t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CONNECT_HINT')
);

const statusLabel = computed(() => {
  switch (sessionStatus.value) {
    case 'stopped':
      return t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.STATUS.STOPPED');
    case 'scan_qr_code':
      return t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.STATUS.SCAN_QR_CODE');
    case 'working':
      return t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.STATUS.WORKING');
    case 'failed':
      return t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.STATUS.FAILED');
    case 'starting':
    default:
      return t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.STATUS.STARTING');
  }
});

const statusClass = computed(() => {
  if (sessionStatus.value === 'working') {
    return 'bg-n-teal-3 text-n-teal-11';
  }

  if (
    sessionStatus.value === 'scan_qr_code' ||
    sessionStatus.value === 'starting'
  ) {
    return 'bg-n-amber-3 text-n-amber-11';
  }

  if (sessionStatus.value === 'failed') {
    return 'bg-n-ruby-3 text-n-ruby-11';
  }

  return 'bg-n-slate-3 text-n-slate-11';
});

const revokeQr = () => {
  if (!qrSrc.value) return;

  URL.revokeObjectURL(qrSrc.value);
  qrSrc.value = '';
};

const resetState = () => {
  revokeQr();
  tab.value = 'qr';
  sessionStatus.value = '';
  phone.value = '';
  pairCode.value = '';
  pairing.value = false;
  qrLoading.value = false;
  qrRefreshIn.value = QR_REFRESH_SECONDS;
  errorMessage.value = '';
  connectedPayload.value = null;
};

const parsePairingCode = data =>
  data?.results?.pair_code ||
  data?.results?.code ||
  data?.pair_code ||
  data?.code ||
  '';

function stopPolling() {
  if (statusTimer.value) window.clearInterval(statusTimer.value);
  if (qrTimer.value) window.clearInterval(qrTimer.value);
  statusTimer.value = null;
  qrTimer.value = null;
}

const loadQr = async () => {
  if (!props.accountId || !props.inboxId || isConnected.value) return;

  qrLoading.value = true;
  errorMessage.value = '';

  try {
    const { data } = await NooviConnectAPI.getQr(
      props.accountId,
      props.inboxId
    );
    const nextQrSrc = URL.createObjectURL(data);
    revokeQr();
    qrSrc.value = nextQrSrc;
    qrRefreshIn.value = QR_REFRESH_SECONDS;
  } catch {
    if (!qrSrc.value) {
      errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.QR_ERROR');
    }
  } finally {
    qrLoading.value = false;
  }
};

const refreshQr = async () => {
  if (!props.accountId || !props.inboxId || isConnected.value) return;

  qrLoading.value = true;
  errorMessage.value = '';

  try {
    await NooviConnectAPI.refreshQr(props.accountId, props.inboxId);
    await loadQr();
  } catch {
    errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.QR_ERROR');
  } finally {
    qrLoading.value = false;
  }
};

const pollStatus = async () => {
  if (!props.accountId || !props.inboxId || connectedPayload.value) return;

  try {
    const { data } = await NooviConnectAPI.getStatus(
      props.accountId,
      props.inboxId
    );
    sessionStatus.value = data.session_status;

    if (data.session_status === 'working') {
      connectedPayload.value = data;
      stopPolling();
      emit('connected', data);
    }
  } catch {
    // Keep polling; transient provider failures should not break the flow.
  }
};

const tickQrRefresh = () => {
  if (isConnected.value || tab.value !== 'qr') return;

  qrRefreshIn.value -= 1;
  if (qrRefreshIn.value <= 0) {
    loadQr();
  }
};

const startPolling = () => {
  stopPolling();
  pollStatus();
  statusTimer.value = window.setInterval(pollStatus, STATUS_POLL_MS);
  qrTimer.value = window.setInterval(tickQrRefresh, 1000);
};

const requestPairCode = async () => {
  if (!digitsPhone.value || !props.accountId || !props.inboxId) return;

  pairing.value = true;
  pairCode.value = '';
  errorMessage.value = '';

  try {
    const { data } = await NooviConnectAPI.requestPairingCode(
      props.accountId,
      props.inboxId,
      digitsPhone.value
    );
    pairCode.value = parsePairingCode(data);
  } catch {
    errorMessage.value = t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CODE_ERROR');
  } finally {
    pairing.value = false;
  }
};

watch(
  () => [props.accountId, props.inboxId],
  () => {
    stopPolling();
    resetState();
    loadQr();
    startPolling();
  },
  { immediate: true }
);

watch(tab, value => {
  if (value === 'qr' && !qrSrc.value) {
    loadQr();
  }
});

onBeforeUnmount(() => {
  stopPolling();
  revokeQr();
});
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <div class="mb-4 flex w-full flex-col items-center gap-2 text-center">
      <h2 class="mb-0 text-lg font-medium text-n-slate-12">
        {{ connectionTitle }}
      </h2>
      <p class="mb-0 max-w-lg text-sm text-n-slate-11">
        {{ connectionHint }}
      </p>
      <span
        class="inline-flex min-h-6 items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
        :class="statusClass"
      >
        {{ statusLabel }}
      </span>
    </div>

    <div
      v-if="errorMessage"
      class="mb-4 w-full rounded-lg border border-n-ruby-5 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div class="mb-4 flex gap-1 rounded-lg bg-n-alpha-1 p-1">
      <button
        type="button"
        class="min-h-8 rounded-md px-4 py-1.5 text-sm font-medium"
        :class="
          tab === 'qr' ? 'bg-n-background text-n-slate-12' : 'text-n-slate-11'
        "
        @click="tab = 'qr'"
      >
        {{ t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.TAB_QR') }}
      </button>
      <button
        type="button"
        class="min-h-8 rounded-md px-4 py-1.5 text-sm font-medium"
        :class="
          tab === 'code' ? 'bg-n-background text-n-slate-12' : 'text-n-slate-11'
        "
        @click="tab = 'code'"
      >
        {{ t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.TAB_CODE') }}
      </button>
    </div>

    <div v-if="tab === 'qr'" class="flex w-full flex-col items-center">
      <div
        class="flex size-64 items-center justify-center rounded-xl border border-n-weak bg-n-background"
      >
        <img
          v-if="qrSrc"
          :src="qrSrc"
          :alt="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.QR_ALT')"
          class="size-60 rounded-lg"
        />
        <span v-else class="text-sm text-n-slate-10">
          {{ t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.QR_LOADING') }}
        </span>
      </div>
      <p class="mb-0 mt-3 text-center text-xs text-n-slate-10">
        {{
          t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.QR_REFRESH_HINT', {
            seconds: qrRefreshIn,
          })
        }}
      </p>
      <Button
        class="mt-3"
        type="button"
        variant="link"
        color="slate"
        size="sm"
        :is-loading="qrLoading"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.REFRESH_QR')"
        @click="refreshQr"
      />
    </div>

    <div v-else class="flex w-full max-w-sm flex-col gap-3">
      <Input
        v-model="phone"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CODE_PHONE_LABEL')"
        :placeholder="
          t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.CODE_PHONE_PLACEHOLDER')
        "
        inputmode="numeric"
        autocomplete="tel"
      />
      <Button
        type="button"
        solid
        blue
        :is-loading="pairing"
        :disabled="!digitsPhone || pairing"
        :label="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.GENERATE_CODE')"
        @click="requestPairCode"
      />
      <div
        v-if="pairCode"
        class="rounded-lg bg-n-alpha-1 p-3 text-center font-mono text-2xl font-bold tracking-widest text-n-slate-12"
      >
        {{ pairCode }}
      </div>
    </div>

    <Button
      v-if="showSkipAction"
      class="mt-6"
      type="button"
      variant="link"
      color="slate"
      :label="t('INBOX_MGMT.ADD.WHATSAPP.NOOVI_CONNECT.SKIP')"
      @click="emit('skip')"
    />
  </div>
</template>
